<script setup lang="ts">
/**
 * Card "Complet — Liste d'attente" affichée quand toutes les places sont vendues
 * et que l'organisateur a activé la waitlist sur l'événement.
 */
interface WaitlistForm {
  name: string;
  email: string;
  phone: string;
}

const props = defineProps<{
  modelValue: WaitlistForm;
  message: { type: "success" | "error"; text: string } | null;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: WaitlistForm): void;
  (e: 'submit'): void;
}>();

const form = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const { t } = useI18n();
</script>

<template>
  <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
    <div class="p-6">
      <div class="text-center mb-5">
        <img src="/svg/hands-no.svg" alt="" class="w-20 h-20 mx-auto mb-3"
          width="106"
          height="198"
        />
        <h3 class="text-lg font-bold text-[#2d2e5c] dark:text-white mb-1">{{ t('billetterie.bslugWaitlistCard.titre') }}</h3>
        <p class="text-sm text-[#717198] dark:text-gray-400">{{ t('billetterie.bslugWaitlistCard.message') }}</p>
      </div>

      <div v-if="message" class="mb-4 p-3 rounded-xl border text-sm" :class="message.type === 'success' ? 'bg-white dark:bg-[#0f0f0f] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400' : 'bg-white dark:bg-[#0f0f0f] border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'">
        {{ message.text }}
      </div>

      <div v-if="!message || message.type === 'error'" class="space-y-3">
        <input v-model="form.name" type="text" :placeholder="t('billetterie.bslugWaitlistCard.placeholderNomComplet')" class="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors" />
        <input v-model="form.email" type="email" :placeholder="t('billetterie.bslugWaitlistCard.placeholderEmail')" class="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors" />
        <button
          class="w-full py-3 accent-btn rounded-xl text-sm font-semibold transition-colors disabled:opacity-40"
          :disabled="!form.name || !form.email || loading"
          @click="$emit('submit')"
        >
          <UIcon v-if="loading" name="i-lucide-loader-2" class="size-4 animate-spin" />
          <span v-else>{{ t('billetterie.bslugWaitlistCard.rejoindreListeAttente') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
